/**
 * Completitud de las fotos obligatorias: la **única** condición que puede impedir cerrar una
 * inspección.
 *
 * Lo define el autor de la historia en el comentario del 2026-08-25, posterior a la actualización
 * de la descripción del mismo día: *«La única condición que debe bloquear la finalización de una
 * inspección es que falte una fotografía o archivo definido como obligatorio. Las validaciones
 * relacionadas con calidad de imagen deben mantenerse únicamente como advertencias, sin impedir el
 * cierre»*. Por eso acá **no se mira `analysisResult` en ninguna parte**: si este módulo llegara a
 * consultar el veredicto, la calidad volvería a bloquear por la puerta de atrás.
 *
 * La foto encolada cuenta como faltante, y esa es la decisión que más pesa: existe en el teléfono
 * pero no en el backend, así que cerrar con ella pendiente entrega una inspección incompleta. Se
 * distingue de la no capturada porque el remedio es distinto —una se reintenta, la otra hay que
 * sacarla— y porque un mensaje que le pide al asegurado volver a fotografiar algo que ya fotografió
 * es la forma más rápida de perder su confianza en el resto de los avisos.
 *
 * Al cerrar, además, S3 confirma que cada archivo que el teléfono dio por subido existe
 * (`utils/StorageCheck.ts`). Una foto presente en el teléfono con un archivo que S3 no tiene falta.
 * El motivo del teléfono gana: S3 solo agrega faltantes entre las fotos que el teléfono da por
 * presentes. Sin respuesta de S3 se decide exactamente como sin esta verificación.
 */
import { storageFileNameOf } from './StorageCheck';

/** Lo mínimo que hace falta de `IPhotoRequired`; se acepta el objeto completo por estructura. */
export interface RequiredPhotoRef {
  id: number;
  title: string;
  saltar_foto: boolean;
}

/** Lo mínimo que hace falta de una entrada de la tabla `Photo`. */
export interface StoredPhotoRef {
  photoId: number;
  files: { data?: unknown; ext?: unknown }[];
}

/** Lo mínimo que hace falta de una fila de `NotUploadedFiles`. */
export interface QueuedPhotoRef {
  photoId: number;
}

export type MissingReason = 'not_captured' | 'captured_not_uploaded' | 'not_in_storage';

export interface MissingRequiredPhoto {
  photoId: number;
  title: string;
  reason: MissingReason;
  /** Solo con `not_in_storage`: los archivos del teléfono que S3 no tiene, que se descartan. */
  missingFileNames?: string[];
}

/**
 * Un archivo cuenta solo si tiene contenido y extensión. Es el mismo criterio que ya aplicaba
 * `getMissingRequiredPhotos` en la pantalla de fotos: una entrada creada por `setHasSeenInstructions`
 * existe con `files: []`, y sin este filtro marcaría como capturada una foto que nadie sacó.
 */
function usableFiles(entry: StoredPhotoRef): StoredPhotoRef['files'] {
  return entry.files.filter((file) => !!file.data && !!file.ext);
}

/**
 * Los archivos usables de cada foto, juntando todas sus filas: el índice de `Photo` no es único por
 * `photoId`, y una foto cuenta como presente si **alguna** de sus filas tiene un archivo usable.
 */
function usableFilesByPhoto(stored: StoredPhotoRef[]): Map<number, StoredPhotoRef['files']> {
  const byPhoto = new Map<number, StoredPhotoRef['files']>();
  for (const entry of stored) {
    byPhoto.set(entry.photoId, [...(byPhoto.get(entry.photoId) ?? []), ...usableFiles(entry)]);
  }
  return byPhoto;
}

/**
 * Los nombres en S3 de los archivos usables de las obligatorias que el teléfono da por presentes. Son
 * los que se preguntan al cerrar: ni las saltadas, ni las faltantes, ni las opcionales.
 */
export function requiredStorageFileNames(
  required: RequiredPhotoRef[],
  stored: StoredPhotoRef[],
): string[] {
  const wanted = new Set(required.filter((photo) => !photo.saltar_foto).map((photo) => photo.id));
  const names = stored
    .filter((entry) => wanted.has(entry.photoId))
    .flatMap((entry) => usableFiles(entry).map((file) => storageFileNameOf(file.data)))
    .filter((name): name is string => name !== null);
  return [...new Set(names)];
}

/**
 * `storage` es lo que confirmó S3, por nombre de archivo, o `undefined` si no hubo respuesta: en ese
 * caso el resultado es exactamente el de sin verificación. Un nombre que S3 no respondió no cuenta
 * como ausente.
 */
export function findMissingRequiredPhotos(
  required: RequiredPhotoRef[],
  stored: StoredPhotoRef[],
  queued: QueuedPhotoRef[] = [],
  storage?: ReadonlyMap<string, boolean>,
): MissingRequiredPhoto[] {
  const filesByPhoto = usableFilesByPhoto(stored);
  const pendingUpload = new Set(queued.map((row) => row.photoId));
  const missing: MissingRequiredPhoto[] = [];

  for (const photo of required) {
    if (photo.saltar_foto) {
      continue;
    }
    const files = filesByPhoto.get(photo.id) ?? [];
    if (files.length === 0) {
      missing.push({
        photoId: photo.id,
        title: photo.title,
        // Sacada pero sin confirmar contra el backend: el remedio es reintentar, no volver a sacarla.
        reason: pendingUpload.has(photo.id) ? 'captured_not_uploaded' : 'not_captured',
      });
      continue;
    }
    if (!storage) {
      continue;
    }
    const absent = files
      .map((file) => storageFileNameOf(file.data))
      .filter((name): name is string => name !== null && storage.get(name) === false);
    if (absent.length > 0) {
      missing.push({ photoId: photo.id, title: photo.title, reason: 'not_in_storage', missingFileNames: absent });
    }
  }
  return missing;
}

/**
 * Mensaje para el asegurado. Sin identificadores internos: el `photo_id` y la OI no le dicen nada y
 * el texto se muestra en pantalla, así que solo van los títulos que él ya vio en el paso.
 */
export function describeMissingRequiredPhotos(
  missing: MissingRequiredPhoto[]
): string {
  const titles = missing.map((photo) => photo.title).join(', ');
  const soloPendientesDeSubida = missing.every(
    (photo) => photo.reason === 'captured_not_uploaded'
  );
  return soloPendientesDeSubida
    ? `Quedaron fotos sin terminar de subir: ${titles}. Revisa tu conexión y vuelve a intentarlo.`
    : `Faltan fotos obligatorias: ${titles}.`;
}
